import React, { useMemo, useState } from "react";
import {
  Checkbox,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  alpha,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ErrorSharpIcon from "@mui/icons-material/ErrorSharp";
import EditIcon from "@mui/icons-material/Edit";
import IconButtonWithTooltip from "../../../utils/IconButtonWithTooltip";
import { TextFieldForViewOnly } from "../../../utils/TextField/TextField";
import {
  FIELD_LABELS,
  displayValue,
  hasInitialAllowance,
  isApplicableType,
  isEnhancedTwdvType,
} from "./rules";
import { formatAmount } from "../../../../utils/hooks/dataHandler";

// Matches FaRegisterDrawer headerGroups field order.
const DRAWER_FIELD_ORDER = [
  "assetCategory",
  "assetDescription",
  "caType",
  "yaOfPurchase",
  "costOfAsset",
  "noOfYearsClaim",
  "deferred",
  "disposal",
  "twdvBf",
  "enhancedTwdvBf",
  "noOfYearsBf",
  "noOfYearsCf",
  "iaRate",
  "ia",
  "aa",
  "twdvCf",
];

const applicableOnlyFields = new Set([
  "noOfYearsClaim",
  "deferred",
  "disposal",
  "twdvBf",
  "noOfYearsBf",
  "noOfYearsCf",
  "aa",
  "twdvCf",
]);

const hideForHirePurchaseFields = new Set(["deferred", "disposal"]);

const booleanFields = new Set(["deferred", "disposal"]);

const numericDisplayFields = new Set([
  "costOfAsset",
  "twdvBf",
  "enhancedTwdvBf",
  "ia",
  "aa",
  "twdvCf",
]);

const COLUMN_META = {
  assetCategory: { align: "left", minWidth: 160, sticky: "left" },
  assetDescription: { align: "left", minWidth: 200, sticky: "left" },
  caType: { align: "left", minWidth: 160 },
  yaOfPurchase: { align: "center", minWidth: 110 },
  costOfAsset: { align: "right", minWidth: 130 },
  noOfYearsClaim: { align: "center", minWidth: 120 },
  deferred: { align: "center", minWidth: 130 },
  disposal: { align: "center", minWidth: 130 },
  twdvBf: { align: "right", minWidth: 120 },
  enhancedTwdvBf: { align: "right", minWidth: 140 },
  noOfYearsBf: { align: "center", minWidth: 110 },
  noOfYearsCf: { align: "center", minWidth: 110 },
  iaRate: { align: "center", minWidth: 90 },
  ia: { align: "right", minWidth: 100 },
  aa: { align: "right", minWidth: 100 },
  twdvCf: { align: "right", minWidth: 120 },
};

const CHECKBOX_WIDTH = 55;
const CATEGORY_WIDTH = 160;
const DESCRIPTION_WIDTH = 200;
const ACTIONS_WIDTH = 120;

const SORTABLE_FIELDS = new Set(["yaOfPurchase", "caType"]);

const compareRows = (a, b, sortField, sortDirection) => {
  const direction = sortDirection === "desc" ? -1 : 1;

  if (sortField === "yaOfPurchase") {
    const aValue = Number(a.yaOfPurchase) || 0;
    const bValue = Number(b.yaOfPurchase) || 0;
    if (aValue !== bValue) return (aValue - bValue) * direction;
  }

  if (sortField === "caType") {
    const aValue = String(a.caType || "").toLowerCase();
    const bValue = String(b.caType || "").toLowerCase();
    if (aValue !== bValue) return aValue.localeCompare(bValue) * direction;
  }

  const aLabel = String(
    a.assetDescription || a.assetCategory || ""
  ).toLowerCase();
  const bLabel = String(
    b.assetDescription || b.assetCategory || ""
  ).toLowerCase();
  return aLabel.localeCompare(bLabel);
};

const sortRows = (items, sortField, sortDirection) => {
  if (!sortField) return items;
  return [...items].sort((a, b) => compareRows(a, b, sortField, sortDirection));
};

const buildDisplaySections = (
  rows,
  sortField,
  sortDirection,
  groupByCategory
) => {
  if (!groupByCategory) {
    return [
      { key: "all", label: "", rows: sortRows(rows, sortField, sortDirection) },
    ];
  }

  const groupMap = new Map();
  rows.forEach((row) => {
    const key = row.assetCategoryId || row.assetCategory || "__uncategorized__";
    const label = String(row.assetCategory || "").trim() || "Uncategorized";

    if (!groupMap.has(key)) {
      groupMap.set(key, { key, label, rows: [] });
    }
    groupMap.get(key).rows.push(row);
  });

  return [...groupMap.values()]
    .sort((a, b) => a.label.localeCompare(b.label))
    .map((group) => ({
      ...group,
      rows: sortRows(group.rows, sortField, sortDirection),
    }));
};

const isFieldApplicable = (row, field, caSettings) => {
  const applicable = isApplicableType(row.caType);
  const isHirePurchase = row.caType === "Hire Purchase";

  if (isHirePurchase && hideForHirePurchaseFields.has(field)) return false;
  if (!applicable && applicableOnlyFields.has(field)) return false;
  if (field === "enhancedTwdvBf") {
    return isEnhancedTwdvType(row.caType, caSettings);
  }
  if (field === "iaRate" || field === "ia") {
    return hasInitialAllowance(row.caType, caSettings);
  }
  return true;
};

const renderCellValue = (row, field, caSettings) => {
  if (!isFieldApplicable(row, field, caSettings)) {
    return <TextFieldForViewOnly value='N.A.' />;
  }

  if (booleanFields.has(field)) {
    return <TextFieldForViewOnly value={row[field] ? "Yes" : "No"} />;
  }

  if (numericDisplayFields.has(field) || field === "iaRate") {
    return (
      <TextFieldForViewOnly value={formatAmount(displayValue(row, field))} />
    );
  }

  if (
    field === "yaOfPurchase" ||
    field === "noOfYearsClaim" ||
    field === "noOfYearsBf" ||
    field === "noOfYearsCf"
  ) {
    const value = displayValue(row, field);
    return <TextFieldForViewOnly value={value === "-" ? "" : value} />;
  }

  return (
    <Typography
      variant='body2'
      color={row[field] ? "text.primary" : "text.secondary"}
      sx={{
        maxWidth: field === "assetDescription" ? 260 : 190,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {row[field] || "-"}
    </Typography>
  );
};

const getStickyBodyBg = (theme, { selected, issueCount }) => {
  const errorBg = alpha(theme.palette.error.main, 0.08);

  if (selected) {
    return issueCount > 0 ? errorBg : "#F5F0FF";
  }

  return issueCount > 0 ? errorBg : theme.palette.background.paper;
};

const FaRegisterTable = ({
  exportRef,
  rows,
  selectedRows,
  activeRowKey,
  fullscreenOpen,
  canEditSchedule,
  visibleFields = DRAWER_FIELD_ORDER,
  caSettings,
  onSelectRow,
  onSelectAll,
  onOpenAsset,
  onDeleteAsset,
  saveAttempted = false,
  validationRows = [],
}) => {
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [groupByCategory, setGroupByCategory] = useState(false);

  const allSelected = rows.length > 0 && selectedRows.size === rows.length;
  const issueCounts = useMemo(() => {
    const map = new Map();
    validationRows.forEach((item) => {
      map.set(item.rowKey, item.errors?.length || 0);
    });
    return map;
  }, [validationRows]);

  const columns = useMemo(
    () =>
      DRAWER_FIELD_ORDER.filter(
        (field) =>
          visibleFields.includes(field) &&
          !(groupByCategory && field === "assetCategory")
      ).map((field) => ({
        field,
        label: FIELD_LABELS[field] || field,
        ...(COLUMN_META[field] || { align: "center", minWidth: 120 }),
      })),
    [groupByCategory, visibleFields]
  );

  const displaySections = useMemo(
    () => buildDisplaySections(rows, sortField, sortDirection, groupByCategory),
    [rows, sortField, sortDirection, groupByCategory]
  );

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }

    setSortField(field);
    setSortDirection("asc");
  };

  const checkboxLeft = 0;
  const hasCategoryColumn = columns.some(
    (column) => column.field === "assetCategory"
  );
  const stickyLeftColumnCount = columns.filter(
    (column) => column.sticky === "left"
  ).length;
  const categoryLeft = canEditSchedule ? CHECKBOX_WIDTH : 0;
  const descriptionLeft =
    categoryLeft + (hasCategoryColumn ? CATEGORY_WIDTH : 0);

  const getStickyLeft = (field) => {
    if (field === "assetCategory") return categoryLeft;
    if (field === "assetDescription") return descriptionLeft;
    return undefined;
  };

  const stickyHeaderSx = (column) => {
    const left = getStickyLeft(column.field);
    const isStickyLeft = left !== undefined;

    return {
      top: 0,
      bgcolor: "background.paper",
      borderBottom: "1px solid",
      borderBottomColor: "divider",
      fontWeight: 700,
      letterSpacing: 0,
      minWidth: column.minWidth,
      whiteSpace: "nowrap",
      ...(isStickyLeft
        ? {
            position: "sticky",
            left,
            zIndex: 5,
            boxShadow:
              column.field === "assetDescription"
                ? "5px 0 5px rgba(0, 0, 0, 0.03)"
                : undefined,
          }
        : {}),
    };
  };

  const stickyBodySx = (column, rowState) => {
    const left = getStickyLeft(column.field);
    const isStickyLeft = left !== undefined;

    return (theme) => ({
      minWidth: column.minWidth,
      bgcolor: getStickyBodyBg(theme, rowState),
      ...(isStickyLeft
        ? {
            position: "sticky",
            left,
            zIndex: 2,
            boxShadow:
              column.field === "assetDescription"
                ? "5px 0 5px rgba(0, 0, 0, 0.03)"
                : undefined,
          }
        : {}),
    });
  };

  const renderColumnHeader = (column) => {
    if (SORTABLE_FIELDS.has(column.field)) {
      return (
        <TableSortLabel
          active={sortField === column.field}
          direction={sortField === column.field ? sortDirection : "asc"}
          onClick={() => handleSort(column.field)}
        >
          {column.label}
        </TableSortLabel>
      );
    }

    return column.label;
  };

  const stickyLeftColSpan = (canEditSchedule ? 1 : 0) + stickyLeftColumnCount;
  const scrollableColSpan = Math.max(columns.length - stickyLeftColumnCount, 0);

  const renderGroupHeader = (section) => {
    const selectedCount = section.rows.filter((row) =>
      selectedRows.has(row.rowKey)
    ).length;
    const allSectionSelected =
      section.rows.length > 0 && selectedCount === section.rows.length;
    const someSectionSelected =
      selectedCount > 0 && selectedCount < section.rows.length;

    const handleSelectSection = (event) => {
      const checked = event.target.checked;
      section.rows.forEach((row) => onSelectRow(row.rowKey, checked));
    };

    return (
      <TableRow
        key={`group-${section.key}`}
        sx={(theme) => ({
          bgcolor: alpha(theme.palette.primary.main, 0.06),
          "& td": {
            borderBottom: "1px solid",
            borderBottomColor: "divider",
          },
        })}
      >
        <TableCell
          sx={{
            position: "sticky",
            zIndex: 3,
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.02),
            py: 1.25,
          }}
        >
          {canEditSchedule && (
            <Checkbox
              size='small'
              checked={allSectionSelected}
              indeterminate={someSectionSelected}
              onClick={(event) => event.stopPropagation()}
              onChange={handleSelectSection}
            />
          )}
        </TableCell>
        <TableCell
          colSpan={stickyLeftColSpan - 1}
          sx={{
            position: "sticky",
            left: checkboxLeft,
            zIndex: 3,
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.02),
            boxShadow: "5px 0 5px rgba(0, 0, 0, 0.03)",
            py: 1.25,
          }}
        >
          <Stack direction='row' alignItems='center' spacing={1}>
            <Typography variant='body2' sx={{ fontWeight: 800 }}>
              {section.label}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              {section.rows.length}{" "}
              {section.rows.length === 1 ? "asset" : "assets"}
            </Typography>
          </Stack>
        </TableCell>
        <TableCell colSpan={scrollableColSpan} />
        <TableCell
          sx={{
            position: "sticky",
            right: 0,
            zIndex: 3,
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
            boxShadow: "-2px 0 5px rgba(0, 0, 0, 0.05)",
            minWidth: ACTIONS_WIDTH,
          }}
        />
      </TableRow>
    );
  };

  const renderAssetRow = (row) => {
    const active = activeRowKey === row.rowKey;
    const selected = selectedRows.has(row.rowKey);
    const issueCount = saveAttempted ? issueCounts.get(row.rowKey) || 0 : 0;
    const rowState = { active, selected, issueCount };

    return (
      <TableRow
        key={row.rowKey}
        hover
        sx={(theme) => ({
          ...(issueCount > 0 && {
            bgcolor: alpha(theme.palette.error.main, 0.04),
            "&:hover": {
              bgcolor: alpha(theme.palette.error.main, 0.08),
            },
            "& > td:first-of-type": {
              borderLeft: `3px solid ${theme.palette.error.main}`,
            },
          }),
          "&.Mui-selected": {
            bgcolor:
              issueCount > 0
                ? alpha(theme.palette.error.main, 0.08)
                : alpha(theme.palette.primary.main, 0.04),
          },
          "&.Mui-selected:hover": {
            bgcolor:
              issueCount > 0
                ? alpha(theme.palette.error.main, 0.12)
                : alpha(theme.palette.primary.main, 0.12),
          },
        })}
      >
        {canEditSchedule && (
          <TableCell
            sx={(theme) => ({
              position: "sticky",
              left: checkboxLeft,
              zIndex: 3,
              minWidth: CHECKBOX_WIDTH,
              width: CHECKBOX_WIDTH,
              bgcolor: getStickyBodyBg(theme, rowState),
            })}
          >
            <Checkbox
              size='small'
              checked={selected}
              onClick={(event) => event.stopPropagation()}
              onChange={(event) =>
                onSelectRow(row.rowKey, event.target.checked)
              }
            />
          </TableCell>
        )}
        {columns.map((column) => (
          <TableCell
            key={column.field}
            align={column.align}
            sx={stickyBodySx(column, rowState)}
          >
            {renderCellValue(row, column.field, caSettings)}
          </TableCell>
        ))}
        <TableCell
          align='center'
          sx={(theme) => ({
            position: "sticky",
            right: 0,
            zIndex: 2,
            bgcolor: getStickyBodyBg(theme, rowState),
            minWidth: ACTIONS_WIDTH,
            boxShadow: "-2px 0 5px rgba(0, 0, 0, 0.05)",
          })}
        >
          <Stack direction='row' justifyContent='center' spacing={0.5}>
            {issueCount > 0 && (
              <IconButtonWithTooltip
                icon={ErrorSharpIcon}
                tooltipTitle={`${issueCount} validation ${
                  issueCount === 1 ? "issue" : "issues"
                }. Click to view.`}
                onClick={() => onOpenAsset(row.rowKey)}
                color='error'
                size='small'
              />
            )}
            <IconButtonWithTooltip
              icon={EditIcon}
              tooltipTitle='Edit Asset'
              onClick={() => onOpenAsset(row.rowKey)}
              color='primary'
              size='small'
            />
            {canEditSchedule && (
              <IconButtonWithTooltip
                icon={DeleteOutlineIcon}
                tooltipTitle='Delete Asset'
                onClick={() => onDeleteAsset(row.rowKey)}
                color='error'
                size='small'
              />
            )}
          </Stack>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Paper
      ref={exportRef}
      elevation={0}
      sx={{
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
        bgcolor: (theme) => alpha(theme.palette.background.paper, 0.98),
        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
        height: fullscreenOpen ? 790 : "750px",
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent='space-between'
        spacing={1}
        sx={{
          p: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: (theme) => alpha(theme.palette.background.default, 0.35),
        }}
      >
        <FormControlLabel
          control={
            <Switch
              size='small'
              checked={groupByCategory}
              onChange={(event) => setGroupByCategory(event.target.checked)}
            />
          }
          label={
            <Typography variant='body2' sx={{ fontWeight: 600 }}>
              Group by Category
            </Typography>
          }
        />
        <Typography variant='caption' color='text.secondary'>
          Sort by clicking the YA or CA Type column headers
        </Typography>
      </Stack>

      <TableContainer
        component={Paper}
        style={{
          display: "block",
          maxWidth: "100%",
          flex: 1,
          overflow: "auto",
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {canEditSchedule && (
                <TableCell
                  sx={{
                    position: "sticky",
                    left: checkboxLeft,
                    top: 0,
                    zIndex: 6,
                    bgcolor: "background.paper",
                    minWidth: CHECKBOX_WIDTH,
                    width: CHECKBOX_WIDTH,
                    borderBottom: "1px solid",
                    borderBottomColor: "divider",
                  }}
                >
                  <Checkbox
                    size='small'
                    checked={allSelected}
                    indeterminate={
                      selectedRows.size > 0 && selectedRows.size < rows.length
                    }
                    onClick={(event) => event.stopPropagation()}
                    onChange={(event) => onSelectAll(event.target.checked)}
                  />
                </TableCell>
              )}
              {columns.map((column) => (
                <TableCell
                  key={column.field}
                  align={column.align}
                  sx={stickyHeaderSx(column)}
                >
                  {renderColumnHeader(column)}
                </TableCell>
              ))}
              <TableCell
                align='center'
                sx={{
                  position: "sticky",
                  right: 0,
                  top: 0,
                  zIndex: 5,
                  bgcolor: "background.paper",
                  borderBottom: "1px solid",
                  borderBottomColor: "divider",
                  minWidth: ACTIONS_WIDTH,
                  fontWeight: 700,
                  letterSpacing: 0,
                  whiteSpace: "nowrap",
                  boxShadow: "-2px 0 5px rgba(0, 0, 0, 0.05)",
                }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displaySections.map((section) => (
              <React.Fragment key={section.key}>
                {groupByCategory && renderGroupHeader(section)}
                {section.rows.map((row) => renderAssetRow(row))}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default FaRegisterTable;
